import React, { useEffect, useMemo, useState } from "react";
import { ShoppingBag, Plus, Minus, Trash2, Package, Truck, CheckCircle2, Clock, X, PackageCheck, Download } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/time";
import { Label, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const money = (n) => `R${Number(n ?? 0).toFixed(2)}`;

const STATUS_UI = {
  pending_payment: { label: "Awaiting EFT", color: "bg-amber-100 text-amber-800", Icon: Clock },
  paid: { label: "Payment received", color: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  in_production: { label: "In production", color: "bg-indigo-100 text-indigo-800", Icon: Package },
  shipped: { label: "Shipped", color: "bg-sky-100 text-sky-800", Icon: Truck },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-800", Icon: PackageCheck },
  cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-500", Icon: X },
};

function StatusChip({ status }) {
  const ui = STATUS_UI[status] || STATUS_UI.pending_payment;
  const Icon = ui.Icon;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${ui.color}`} data-testid={`order-status-${status}`}><Icon className="h-3.5 w-3.5" />{ui.label}</span>;
}

function tierDiscount(tiers, qty) {
  const sorted = [...(tiers || [])].sort((a, b) => b.min_qty - a.min_qty);
  const hit = sorted.find((t) => qty >= t.min_qty);
  return hit ? hit.discount_pct : 0;
}

function variantPrice(product, variant) {
  if (!variant) return product.base_price;
  return variant.price_override != null ? variant.price_override : product.base_price;
}

function ProductCard({ product, employees, tiers, onAdd }) {
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(1);
  const [empId, setEmpId] = useState("");
  const options = useMemo(() => product.options || [], [product.options]);
  const variants = useMemo(() => product.variants || [], [product.variants]);
  const canPickEmployee = product.category === "lanyard";

  const variant = useMemo(() => {
    if (!variants.length) return null;
    if (!options.length) return variants[0];
    return variants.find((v) => options.every((o) => (v.option_values || {})[o.name] === selected[o.name])) || null;
  }, [variants, options, selected]);

  const unit = variantPrice(product, variant);
  const outOfStock = variant && typeof variant.stock === "number" && variant.stock <= 0;
  const pct = tierDiscount(tiers, qty);
  const lineTotal = Math.max(0, unit * qty * (1 - pct / 100));

  const add = () => {
    if (variants.length && !variant) { toast.error("Pick each option before adding"); return; }
    if (outOfStock) { toast.error("Out of stock"); return; }
    const emp = canPickEmployee && empId ? employees.find((e) => e.id === empId) : null;
    onAdd({
      product_id: product.id, product_name: product.name,
      variant_sku: variant ? variant.sku : product.id,
      variant_name: variant ? variant.name : "Standard",
      unit_price: unit, quantity: qty, tier_discount_pct: pct,
      line_total: Number(lineTotal.toFixed(2)),
      employee_id: emp?.id, display_name: emp?.name,
    });
    toast.success(`${product.name} added to cart`);
    setQty(1); setEmpId(""); setSelected({});
  };

  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`product-${product.id}`}>
      {product.image ? <img src={product.image} alt={product.name} className="mb-3 h-32 w-full rounded-xl object-cover" />
        : <div className="mb-3 flex h-32 items-center justify-center rounded-xl bg-slate-100"><Package className="h-10 w-10 text-slate-300" /></div>}
      <p className="font-head text-lg font-bold text-slate-900">{product.name}</p>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">{product.description}</p>

      <div className="mt-4 space-y-3">
        {options.map((opt) => (
          <div key={opt.name}>
            <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">{opt.name}</p>
            <div className="flex flex-wrap gap-1.5">
              {opt.values.map((v) => (
                <button key={v} type="button" onClick={() => setSelected((s) => ({ ...s, [opt.name]: v }))}
                  data-testid={`option-${product.id}-${opt.name}-${v}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${selected[opt.name] === v ? "bg-[#4338CA] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        ))}

        {canPickEmployee && employees.length > 0 && (
          <div>
            <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Personalise for employee <span className="font-normal text-slate-400">(optional)</span></p>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid={`employee-${product.id}`}><SelectValue placeholder="— unassigned —" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-1 py-0.5">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid={`qty-dec-${product.id}`} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-200"><Minus className="h-3.5 w-3.5" /></button>
            <span className="min-w-[2ch] text-center font-head text-sm font-bold" data-testid={`qty-${product.id}`}>{qty}</span>
            <button type="button" onClick={() => setQty((q) => Math.min(5000, q + 1))} data-testid={`qty-inc-${product.id}`} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-200"><Plus className="h-3.5 w-3.5" /></button>
          </div>
          <div className="text-right">
            <p className="font-head text-xl font-bold text-slate-900" data-testid={`unit-price-${product.id}`}>{money(unit)}<span className="text-xs font-semibold text-slate-400"> ea</span></p>
            {pct > 0 && <p className="text-[11px] font-bold text-emerald-600">−{pct}% bulk · {money(lineTotal)}</p>}
          </div>
        </div>

        <Button onClick={add} disabled={outOfStock || (variants.length > 0 && !variant)} data-testid={`add-${product.id}`} className="w-full bg-[#4338CA] hover:bg-[#3730A3]">
          <ShoppingBag className="mr-1 h-4 w-4" /> {outOfStock ? "Out of stock" : "Add to cart"}
        </Button>
        {variant?.stock != null && variant.stock > 0 && <p className="text-center text-[11px] text-slate-400">{variant.stock} in stock</p>}
      </div>
    </div>
  );
}

function CartSummary({ cart, tiers, shipping, onInc, onDec, onRemove, onCheckout }) {
  const subtotal = cart.reduce((s, c) => s + c.unit_price * c.quantity, 0);
  const discount = cart.reduce((s, c) => s + c.unit_price * c.quantity * (c.tier_discount_pct / 100), 0);
  const ship = subtotal - discount >= shipping.free_over ? 0 : shipping.flat;
  const total = subtotal - discount + ship;

  if (!cart.length) {
    return <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center"><ShoppingBag className="mx-auto h-10 w-10 text-slate-300" />
      <p className="mt-3 text-sm text-slate-500">Your cart is empty. Add branded hardware from the catalogue to get started.</p></div>;
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="cart-summary">
      <Label>Your cart</Label>
      <div className="mt-4 divide-y divide-slate-100">
        {cart.map((c, i) => {
          const key = `${c.variant_sku}-${c.employee_id || "x"}-${i}`;
          return (
            <div key={key} className="flex items-center gap-3 py-3" data-testid={`cart-row-${c.variant_sku}`}>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{c.product_name}</p>
                <p className="text-xs text-slate-500">{c.variant_name}{c.display_name ? ` · for ${c.display_name}` : ""}</p>
                {c.tier_discount_pct > 0 && <p className="text-[11px] font-bold text-emerald-600">−{c.tier_discount_pct}% bulk discount</p>}
              </div>
              <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-1 py-0.5">
                <button type="button" onClick={() => onDec(i)} data-testid={`cart-dec-${i}`} className="rounded-full p-1 text-slate-500 hover:bg-slate-200"><Minus className="h-3.5 w-3.5" /></button>
                <span className="min-w-[2ch] text-center text-sm font-bold">{c.quantity}</span>
                <button type="button" onClick={() => onInc(i)} data-testid={`cart-inc-${i}`} className="rounded-full p-1 text-slate-500 hover:bg-slate-200"><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <div className="w-24 text-right font-head font-bold text-slate-900">{money(c.unit_price * c.quantity * (1 - c.tier_discount_pct / 100))}</div>
              <button type="button" onClick={() => onRemove(i)} data-testid={`cart-remove-${i}`} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
      <div className="mt-4 space-y-1 border-t border-slate-100 pt-4 text-sm">
        <div className="flex justify-between text-slate-600"><span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
        {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Bulk discount</span><span data-testid="cart-discount">−{money(discount)}</span></div>}
        <div className="flex justify-between text-slate-600"><span>Shipping</span><span data-testid="cart-shipping">{ship === 0 ? "Free" : money(ship)}</span></div>
        <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 font-head text-lg font-bold text-slate-900"><span>Total</span><span data-testid="cart-total">{money(total)}</span></div>
        {subtotal - discount < shipping.free_over && <p className="text-[11px] text-slate-400">Add {money(shipping.free_over - (subtotal - discount))} more to unlock free shipping.</p>}
      </div>
      <Button onClick={onCheckout} data-testid="go-checkout" className="mt-5 w-full bg-[#4338CA] hover:bg-[#3730A3]">Continue to checkout</Button>
    </div>
  );
}

function CheckoutForm({ cart, tiers, shipping, eft, onDone, onBack }) {
  const [addr, setAddr] = useState({ recipient: "", company: "", street: "", city: "", postal_code: "", country: "South Africa", phone: "", delivery_note: "" });
  const [poNumber, setPoNumber] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const subtotal = cart.reduce((s, c) => s + c.unit_price * c.quantity, 0);
  const discount = cart.reduce((s, c) => s + c.unit_price * c.quantity * (c.tier_discount_pct / 100), 0);
  const ship = subtotal - discount >= shipping.free_over ? 0 : shipping.flat;
  const total = subtotal - discount + ship;
  const required = ["recipient", "street", "city", "postal_code"];
  const ok = required.every((k) => (addr[k] || "").trim().length > 0);

  const place = async () => {
    if (!ok) { toast.error("Fill in all required address fields"); return; }
    setBusy(true);
    try {
      const items = cart.map((c) => ({ product_id: c.product_id, variant_sku: c.variant_sku, quantity: c.quantity,
                                        employee_id: c.employee_id, display_name: c.display_name }));
      const { data } = await api.post("/merchandise/orders", { items, shipping_address: addr, po_number: poNumber || null, order_notes: orderNotes || null });
      toast.success(`Order ${data.number} placed — EFT details emailed`);
      onDone(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const F = (k, label, extra = {}) => (
    <div><p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}{required.includes(k) ? " *" : ""}</p>
      <Input value={addr[k]} onChange={(e) => setAddr((a) => ({ ...a, [k]: e.target.value }))} data-testid={`addr-${k}`} {...extra} /></div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <Label>Shipping address</Label>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {F("recipient", "Recipient name")}{F("company", "Company (optional)")}
            {F("street", "Street address", { placeholder: "123 Rivonia Road, Suite 4" })}{F("city", "City")}
            {F("postal_code", "Postal code")}{F("country", "Country")}
            {F("phone", "Phone (for courier)", { placeholder: "+27…" })}
            <div className="sm:col-span-2"><p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Delivery note (optional)</p>
              <Textarea value={addr.delivery_note} onChange={(e) => setAddr((a) => ({ ...a, delivery_note: e.target.value }))} rows={2} data-testid="addr-delivery_note" /></div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <Label>Order details</Label>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div><p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">PO number (optional)</p>
              <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} data-testid="addr-po_number" /></div>
          </div>
          <div className="mt-3"><p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Notes for our fulfilment team (optional)</p>
            <Textarea value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} rows={2} data-testid="addr-order_notes" placeholder="E.g. please double-pack lanyards" /></div>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm" data-testid="eft-block">
          <Label className="text-amber-700">Pay by EFT</Label>
          <p className="mt-2 text-amber-800">Place your order and we'll email you the invoice. Once your EFT reflects we move production immediately.</p>
          <div className="mt-3 grid gap-x-6 gap-y-1 text-xs text-amber-900 sm:grid-cols-2">
            <div><strong>Bank:</strong> {eft.bank}</div>
            <div><strong>Account:</strong> {eft.account_name}</div>
            <div><strong>Account #:</strong> {eft.account_number}</div>
            <div><strong>Branch:</strong> {eft.branch_code}</div>
            <div className="sm:col-span-2"><strong>Reference:</strong> use the order number we email you</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="checkout-summary">
        <Label>Order summary</Label>
        <div className="mt-3 space-y-2 text-sm">
          {cart.map((c, i) => {
            const line = c.unit_price * c.quantity * (1 - c.tier_discount_pct / 100);
            const key = `${c.variant_sku}-${c.employee_id || "x"}-${i}`;
            return (
              <div key={key} className="flex justify-between gap-3 border-b border-slate-100 pb-2 last:border-0">
                <span className="flex-1 text-slate-700">{c.quantity}× {c.product_name}<span className="block text-xs text-slate-400">{c.variant_name}{c.display_name ? ` · ${c.display_name}` : ""}</span></span>
                <span className="font-head font-bold text-slate-900">{money(line)}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-4 space-y-1 border-t border-slate-100 pt-4 text-sm">
          <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{money(subtotal)}</span></div>
          {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Bulk discount</span><span>−{money(discount)}</span></div>}
          <div className="flex justify-between text-slate-600"><span>Shipping</span><span>{ship === 0 ? "Free" : money(ship)}</span></div>
          <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 font-head text-lg font-bold text-slate-900"><span>Total</span><span>{money(total)}</span></div>
        </div>
        <Button onClick={place} disabled={busy || !ok} data-testid="place-order" className="mt-5 w-full bg-[#4338CA] hover:bg-[#3730A3]">{busy ? "Placing order…" : "Place order"}</Button>
        <button type="button" onClick={onBack} className="mt-3 w-full text-xs font-semibold text-slate-500 hover:text-slate-800">← Back to cart</button>
      </div>
    </div>
  );
}

function OrderCard({ o, onOpen, onDownload }) {
  const addr = o.shipping_address || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`order-${o.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="font-head text-lg font-bold text-slate-900">{o.number}</span>
          <StatusChip status={o.status} />
        </div>
        <span className="font-head text-lg font-bold text-slate-900">{money(o.total)}</span>
      </div>
      <p className="mt-1 text-xs text-slate-400">{fmtDateTime(o.created_at)} · {o.items.length} item(s) · ships to {addr.city || "—"}</p>
      {o.courier && o.tracking_number && <p className="mt-2 text-xs text-slate-600"><strong>{o.courier}</strong> · tracking <code className="rounded bg-slate-100 px-1.5 py-0.5">{o.tracking_number}</code></p>}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => onOpen(o)} className="text-xs font-bold text-indigo-600 hover:underline" data-testid={`order-open-${o.id}`}>View details →</button>
        <button type="button" onClick={() => onDownload(o)} className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:text-indigo-600 hover:underline" data-testid={`order-invoice-${o.id}`}>
          <Download className="h-3.5 w-3.5" /> Invoice PDF
        </button>
      </div>
    </div>
  );
}

function OrderDetail({ order, onClose, onDownload }) {
  if (!order) return null;
  const addr = order.shipping_address || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} data-testid="order-detail-sheet">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Order {order.number}</p>
            <h2 className="font-head text-2xl font-bold text-slate-900">{money(order.total)} · <StatusChip status={order.status} /></h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onDownload(order)} data-testid="order-detail-invoice"><Download className="mr-1 h-4 w-4" />Invoice PDF</Button>
            <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="mt-5 space-y-5">
          <div className="rounded-xl bg-slate-50 p-4 text-sm">
            <p className="font-head font-bold text-slate-900">Shipping</p>
            <p className="mt-1 text-slate-700">{addr.recipient}{addr.company ? ` · ${addr.company}` : ""}</p>
            <p className="text-slate-600">{addr.street}, {addr.city} {addr.postal_code}, {addr.country}</p>
            {addr.phone && <p className="text-slate-600">{addr.phone}</p>}
            {order.courier && <p className="mt-2 text-slate-700"><strong>{order.courier}</strong> · {order.tracking_number}</p>}
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <p className="font-head font-bold text-slate-900">Items</p>
            <div className="mt-2 divide-y divide-slate-100 text-sm">
              {order.items.map((it) => (
                <div key={`${it.variant_sku}-${it.employee_id || "x"}`} className="flex justify-between gap-3 py-2">
                  <span className="flex-1 text-slate-700">{it.quantity}× {it.product_name}<span className="block text-xs text-slate-400">{it.variant_name}{it.display_name ? ` · ${it.display_name}` : ""}</span></span>
                  <span className="font-head font-bold text-slate-900">{money(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Bulk discount</span><span>−{money(order.discount)}</span></div>}
              <div className="flex justify-between text-slate-600"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : money(order.shipping)}</span></div>
              <div className="flex justify-between font-head font-bold text-slate-900"><span>Total</span><span>{money(order.total)}</span></div>
            </div>
          </div>

          <div>
            <p className="font-head font-bold text-slate-900">Timeline</p>
            <ol className="mt-2 space-y-2 text-sm">
              {(order.status_history || []).map((h) => {
                const ui = STATUS_UI[h.status] || STATUS_UI.pending_payment;
                const Icon = ui.Icon;
                return (
                  <li key={`${h.at}-${h.status}`} className="flex items-start gap-3">
                    <span className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${ui.color}`}><Icon className="h-4 w-4" /></span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{ui.label}</p>
                      <p className="text-xs text-slate-500">{fmtDateTime(h.at)} · {h.by}</p>
                      {h.note && <p className="mt-1 text-xs text-slate-600">{h.note}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Merchandise() {
  const [cat, setCat] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState([]);
  const [step, setStep] = useState("catalogue"); // catalogue | checkout
  const [tab, setTab] = useState("shop");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/merchandise/catalogue").then((r) => setCat(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/merchandise/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const addToCart = (item) => setCart((c) => [...c, item]);
  const incItem = (i) => setCart((c) => c.map((it, idx) => idx === i ? { ...it, quantity: Math.min(5000, it.quantity + 1) } : it));
  const decItem = (i) => setCart((c) => c.map((it, idx) => idx === i ? { ...it, quantity: Math.max(1, it.quantity - 1) } : it));
  const removeItem = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const refreshOrders = () => api.get("/merchandise/orders").then((r) => setOrders(r.data)).catch(() => {});
  const afterOrder = () => { setCart([]); setStep("catalogue"); setTab("orders"); refreshOrders(); };

  const downloadInvoice = async (order) => {
    try {
      const { data } = await api.get(`/merchandise/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${order.number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Invoice downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!cat) return <div className="text-slate-400">Loading catalogue…</div>;

  return (
    <div className="space-y-6">
      <div><Label>Physical Identity</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Merchandise Studio</h1>
        <p className="mt-1 text-sm text-slate-500">Order branded QR lanyards, NFC stickers and point-of-service cards for your team.</p></div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setStep("catalogue"); }}>
        <TabsList data-testid="merch-tabs">
          <TabsTrigger value="shop" data-testid="tab-shop">Shop</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">My orders{orders.length ? ` · ${orders.length}` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="shop" className="mt-5">
          {step === "catalogue" ? (
            <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
              <div className="grid gap-4 sm:grid-cols-2">
                {cat.products.map((p) => <ProductCard key={p.id} product={p} employees={employees} tiers={cat.tier_pricing} onAdd={addToCart} />)}
              </div>
              <div className="space-y-4">
                <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 text-xs text-indigo-800" data-testid="tier-info">
                  <p className="font-head font-bold text-indigo-900">Bulk pricing</p>
                  <p className="mt-1">Order more, save more — applied automatically per line.</p>
                  <ul className="mt-2 space-y-0.5">
                    {[...cat.tier_pricing].sort((a, b) => a.min_qty - b.min_qty).map((t) => (
                      <li key={t.min_qty}><strong>{t.min_qty}+</strong> {t.discount_pct > 0 ? `· ${t.discount_pct}% off` : "· full price"}</li>
                    ))}
                  </ul>
                  <p className="mt-2">Free shipping over {money(cat.shipping.free_over)} · otherwise {money(cat.shipping.flat)}.</p>
                </div>
                <CartSummary cart={cart} tiers={cat.tier_pricing} shipping={cat.shipping}
                  onInc={incItem} onDec={decItem} onRemove={removeItem} onCheckout={() => setStep("checkout")} />
              </div>
            </div>
          ) : (
            <CheckoutForm cart={cart} tiers={cat.tier_pricing} shipping={cat.shipping} eft={cat.eft}
              onBack={() => setStep("catalogue")} onDone={afterOrder} />
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-5">
          {orders.length === 0 ? <EmptyState icon={Package} title="No orders yet" hint="Place your first merchandise order from the Shop tab." />
            : <div className="space-y-3">{orders.map((o) => <OrderCard key={o.id} o={o} onOpen={setDetail} onDownload={downloadInvoice} />)}</div>}
        </TabsContent>
      </Tabs>

      <OrderDetail order={detail} onClose={() => setDetail(null)} onDownload={downloadInvoice} />
    </div>
  );
}
